import pino from 'pino';

const isProduction = import.meta.env.VITE_MODE === 'development';

const LOG_COLORS: { [key: string]: string } = {
  trace: '#808080', // Gray
  debug: '#1E90FF', // Dodger Blue
  info: '#33A1E0', // Medium Sea Green
  success: '#4CAF50', // Success Green (Custom Level)
  warn: '#FFD700', // Gold
  error: '#DC143C', // Crimson
  fatal: '#8A2BE2', // Blue Violet
};

const levelToName: { [key: number]: string } = {
  10: 'trace',
  20: 'debug',
  30: 'info',
  35: 'success',
  40: 'warn',
  50: 'error',
  60: 'fatal',
};

const customBrowserWriter = (o: any) => {
  if (isProduction) return;
  const { time, level, msg, ...context } = o;
  const levelName = levelToName[level] || 'unknown';
  const color = LOG_COLORS[levelName] || '#000000';
  const timestamp = new Date(time).toISOString();
  const formattedLog = `%c[${timestamp}] [${levelName.toUpperCase()}]%c ${msg}`;

  console.log(
    formattedLog,
    `color: ${color}; font-weight: bold;`,
    'color: inherit; font-weight: normal;',
    Object.keys(context).length > 0 ? context : ''
  );
};

export enum ErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  API_SERVER_ERROR = 'API_SERVER_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  RATE_LIMIT = 'RATE_LIMIT',
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  UNKNOWN = 'UNKNOWN',
  UI_RENDER_ERROR = 'UI_RENDER_ERROR',
}

export class AppError extends Error {
  constructor(
    public code: ErrorCode,
    message: string,
    public statusCode?: number,
    public context?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';
  }
}

console.log('ENVIRONMENT', { isProduction });

const envLevel = import.meta.env.VITE_LOG_LEVEL?.toLowerCase();
const defaultLevel = isProduction ? 'silent' : 'debug';

export const LoggerService = pino({
  level: envLevel || defaultLevel,

  customLevels: {
    success: 35,
  },

  browser: {
    write: customBrowserWriter,
    asObject: true,
  },

  redact: {
    paths: [
      'password',
      'token',
      '*.password',
      '*.token',
      'apiKey',
      'secret',
      'authorization',
      'creditCard',
      '*.creditCard',
      'ssn',
    ],
    remove: true,
  },
  base: {
    env: import.meta.env.VITE_MODE,
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  },
});

/**
 * Helper to safely log message + optional context object.
 */
export function logTrace(message: string, context?: Record<string, any>) {
  LoggerService.trace(context ?? {}, message);
}

export function logDebug(message: string, context?: Record<string, any>) {
  LoggerService.debug(context ?? {}, message);
}

export function logInfo(message: string, context?: Record<string, any>) {
  LoggerService.info(context ?? {}, message);
}

// Helper for the custom SUCCESS level
export function logSuccess(message: string, context?: Record<string, any>) {
  (LoggerService as any).success(context ?? {}, message);
}

export function logWarn(
  message: string,
  context?: Record<string, any>,
  file?: string,
  func?: string
) {
  LoggerService.warn(
    {
      ...(!isProduction && file ? { file } : {}),
      ...(!isProduction && func ? { function: func } : {}),
      ...context,
    },
    message
  );
}

export function logError(
  message: string,
  context?: Record<string, any>,
  file?: string,
  func?: string
) {
  LoggerService.error(
    {
      ...(!isProduction && file ? { file } : {}),
      ...(!isProduction && func ? { function: func } : {}),
      ...context,
    },
    message
  );
}

export function logFatal(
  message: string,
  context?: Record<string, any>,
  file?: string,
  func?: string
) {
  LoggerService.fatal(
    {
      ...(!isProduction && file ? { file } : {}),
      ...(!isProduction && func ? { function: func } : {}),
      ...context,
    },
    message
  );
}

/**
 * Generic helper for logging with an optional ErrorCode and context.
 */
export function logWithCode(
  level: 'info' | 'warn' | 'error' | 'fatal' | 'debug' | 'trace' | 'success',
  message: string,
  code?: ErrorCode,
  context?: Record<string, any>
) {
  (LoggerService as any)[level]({ code, ...context }, message);
}

/**
 * Multi-business support (Phase 1) — deprecated-field-read instrumentation.
 *
 * Logs a warning whenever the frontend falls back to a deprecated
 * `providerAccount.*` field because the authoritative Business collection
 * data is not yet available in the API response.
 *
 * Per approved_architecture.md §2.4.D (site 21): during the dual-write
 * transition, the user API response should include Business collection data.
 * This instrumentation tracks how often the fallback is used so the team
 * knows when all consumers have migrated and the dual-write can be retired.
 */
export function logDeprecatedFieldRead(
  field: string,
  component: string,
  context?: Record<string, any>
): void {
  logWarn(
    `[DEPRECATED-FIELD-READ] ${component} read deprecated providerAccount.${field} — Business collection data not available`,
    { deprecatedField: field, component, ...context },
    undefined,
    undefined
  );
}
